/**
 * Auth Callback Page
 * Handles the redirect from AliceBlue OAuth flow
 */

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login } from '../redux/slices/authSlice';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const APP_CODE = process.env.REACT_APP_ALICEBLUE_APP_CODE || 'rYF3MOqmmn';

const AuthCallback = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('Processing...');
  const [error, setError] = useState(null);

  useEffect(() => {
    const authCode = searchParams.get('authCode') || searchParams.get('code');
    let userId = searchParams.get('userId');
    const urlAppCode = searchParams.get('appcode') || searchParams.get('appCode');
    
    // If userId not in URL, try to get it from sessionStorage
    if (!userId) {
      userId = sessionStorage.getItem('pendingUserId');
    }

    console.log('AuthCallback - authCode:', authCode, 'userId:', userId, 'urlAppCode:', urlAppCode);
    console.log('Full URL:', window.location.href);
    console.log('All URL params:', Object.fromEntries(searchParams.entries()));

    if (authCode && userId) {
      // Clear the pending userId
      sessionStorage.removeItem('pendingUserId');
      // Automatically login with the auth code
      handleAutoLogin(userId, authCode, urlAppCode || APP_CODE);
    } else if (authCode && !userId) {
      setError('Auth code received but User ID is missing. Please go back to login and enter your User ID.');
      setStatus('Missing User ID');
      // Don't auto-redirect on error
    } else {
      setError('No auth code found in URL. The URL should contain an authCode parameter.');
      setStatus('No auth code found');
      // Don't auto-redirect - let user see the error
    }
  }, []);

  const handleAutoLogin = async (userId, authCode, appCodeToUse) => {
    try {
      setStatus(`Validating access for user: ${userId}...`);
      
      // First validate user access
      const accessResponse = await axios.post(`${API_URL}/api/user-access/validate-access`, {
        userId: userId
      });

      if (!accessResponse.data.success) {
        setError(accessResponse.data.message || 'Access denied. Please contact administrator.');
        setStatus('❌ Access validation failed');
        console.error('Access denied for user:', userId);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // If access is valid, proceed with login
      setStatus(`Logging in user: ${userId} with authCode: ${authCode.substring(0, 10)}...`);
      console.log('Access validated, auto-login with authCode:', authCode, 'appCode:', appCodeToUse);
      
      const result = await dispatch(login({ userId, authCode, appCode: appCodeToUse }));
      console.log('Login result:', result);
      
      if (result.type === 'auth/login/fulfilled') {
        setStatus('✅ Login successful! Redirecting to dashboard...');
        console.log('Login successful, navigating to dashboard');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        const errorMsg = result.payload?.error || result.error?.message || 'Authentication failed';
        setError(errorMsg);
        console.error('Login failed:', errorMsg, 'Full result:', result);
        setStatus('❌ Login failed');
        // Don't auto-redirect on failure
      }
    } catch (err) {
      console.error('Exception during login:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Unknown error occurred';
      setError(errorMsg);
      setStatus('❌ Your Client ID Is Not Registered / May Have Expired. Contact The Admininistrator.');
      // Don't auto-redirect on failure
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 p-4">
      <div className="bg-white p-8 rounded-lg shadow-2xl text-center max-w-2xl w-full">
        {!error && (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
        )}
        
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          {error ? '❌ Authentication Error' : '🔄 Authenticating...'}
        </h2>
        
        <p className="text-gray-600 mb-4 font-medium">{status}</p>

        
        {error && (
          <button
            onClick={() => navigate('/login')}
            className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-lg w-full mb-3"
          >
            ← Go Back to Login Page
          </button>
        )}
        
        <div className="mt-4 p-4 bg-gray-100 rounded-lg text-left">
          
          <div className="text-xs text-gray-600 space-y-1">
           
            <p><strong>User ID:</strong> <code className="bg-white px-2 py-1 rounded">{searchParams.get('userId') || sessionStorage.getItem('pendingUserId') || '❌ Not found'}</code></p>
           
          </div>
        </div>

        {!error && (
          <p className="mt-4 text-xs text-gray-500">
            This page will stay visible so you can see any errors. No automatic redirects on error.
          </p>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
